import React, { useMemo, useState, useEffect } from 'react';
import KargoFirmasiField from './KargoFirmasiField';
import './GonderimDetayModal.css';

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const DURUM_LABEL = {
  OnayBekliyor: 'Onay Bekliyor',
  KargoBekliyor: 'Kargo Bekliyor',
  Kargoda: 'Kargoda',
  GonderenTamamlandi: 'Gönderen Tamamlandı',
  AliciBekliyor: 'Alıcı Bekliyor',
  Tamamlandi: 'Tamamlandı',
  IptalEdildi: 'İptal Edildi',
};

const statusClass = (durum) => {
  if (durum === 'Tamamlandi') return 'status-tamam';
  if (durum === 'IptalEdildi') return 'status-iptal';
  if (durum === 'OnayBekliyor') return 'status-onay';
  return 'status-aktif';
};

const GonderimDetayModal = ({
  gonderim,
  onClose,
  nested = false,
  isAlici = false,
  canEnterKargo = false,
  onKargoKaydet,
  onTeslimAl,
  actionLoading = false,
}) => {
  const data = useMemo(() => gonderim?.veri || gonderim || null, [gonderim]);
  const pozisyonlar = useMemo(
    () => (Array.isArray(data?.pozisyonlar) ? data.pozisyonlar : []),
    [data?.pozisyonlar],
  );
  const [kargoFirmasi, setKargoFirmasi] = useState('');
  const [kargoTakipNo, setKargoTakipNo] = useState('');

  useEffect(() => {
    setKargoFirmasi('');
    setKargoTakipNo('');
  }, [data?.id, canEnterKargo]);

  if (!data) return null;

  const durum = data.durum || 'OnayBekliyor';
  const kargo = data.kargoBilgisi || {};

  const showKargoForm = canEnterKargo && durum === 'KargoBekliyor' && onKargoKaydet;
  const canTeslimAl =
    isAlici &&
    ['AliciBekliyor', 'GonderenTamamlandi', 'Kargoda'].includes(durum) &&
    onTeslimAl;

  const handleKargoSubmit = async (e) => {
    e.preventDefault();
    if (!kargoFirmasi.trim() || !kargoTakipNo.trim()) {
      window.showNotification?.('error', 'Kargo firması ve takip numarası zorunludur');
      return;
    }
    await onKargoKaydet({ kargoFirmasi: kargoFirmasi.trim(), kargoTakipNo: kargoTakipNo.trim() });
  };

  return (
    <div
      className={`modal-overlay gonderim-detay-modal${nested ? ' gonderim-detay-modal--nested' : ''}`}
      onClick={onClose}
      role="presentation"
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-truck-loading" style={{ marginRight: 8 }} aria-hidden="true" />
            Gönderim Detayı {data.gonderimNumarasi ? `— ${data.gonderimNumarasi}` : ''}
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body">
          <div className="gonderim-detay-grid">
            <div className="gonderim-detay-field">
              <span className="label">Gönderim No</span>
              <span className="value highlight">{data.gonderimNumarasi || '—'}</span>
            </div>
            <div className="gonderim-detay-field">
              <span className="label">Durum</span>
              <span className={`value status-badge ${statusClass(durum)}`}>
                {DURUM_LABEL[durum] || durum}
              </span>
            </div>
            <div className="gonderim-detay-field">
              <span className="label">Gönderen</span>
              <span className="value">{data.gonderenTeknikServisAdi || '—'}</span>
            </div>
            <div className="gonderim-detay-field">
              <span className="label">Alıcı</span>
              <span className="value">{data.aliciTeknikServisAdi || '—'}</span>
            </div>
            <div className="gonderim-detay-field" style={{ gridColumn: '1 / -1' }}>
              <span className="label">Alıcı Adresi</span>
              <span className="value">{data.aliciTeknikServisAdres || '—'}</span>
            </div>
            {(data.aliciTeknikServisTelefon || data.aliciTeknikServisYetkiliTelefon) && (
              <div className="gonderim-detay-field" style={{ gridColumn: '1 / -1' }}>
                <span className="label">Alıcı İletişim</span>
                <span className="value">
                  {data.aliciTeknikServisTelefon || '—'}
                  {data.aliciTeknikServisYetkiliKisi
                    ? ` · ${data.aliciTeknikServisYetkiliKisi}${data.aliciTeknikServisYetkiliTelefon ? ` (${data.aliciTeknikServisYetkiliTelefon})` : ''}`
                    : (data.aliciTeknikServisYetkiliTelefon ? ` · ${data.aliciTeknikServisYetkiliTelefon}` : '')}
                </span>
              </div>
            )}
            <div className="gonderim-detay-field">
              <span className="label">Oluşturma</span>
              <span className="value">{formatDateTime(data.olusturmaTarihi)}</span>
            </div>
            {data.onayTarihi && (
              <div className="gonderim-detay-field">
                <span className="label">Onay</span>
                <span className="value">{formatDateTime(data.onayTarihi)}</span>
              </div>
            )}
            {kargo.kargoFirmasi && (
              <>
                <div className="gonderim-detay-field">
                  <span className="label">Kargo Firması</span>
                  <span className="value">{kargo.kargoFirmasi}</span>
                </div>
                <div className="gonderim-detay-field">
                  <span className="label">Takip No</span>
                  <span className="value">{kargo.kargoTakipNo || '—'}</span>
                </div>
              </>
            )}
            {data.not && (
              <div className="gonderim-detay-field" style={{ gridColumn: '1 / -1' }}>
                <span className="label">Not</span>
                <span className="value">{data.not}</span>
              </div>
            )}
          </div>

          {showKargoForm && (
            <form className="gonderim-detay-kargo-form" onSubmit={handleKargoSubmit}>
              <h3>Kargo Bilgisi</h3>
              <p className="gonderim-detay-kargo-hint">
                Kargoya verdiğinizde firma ve takip numarasını girin; gönderim alıcıya iletilir.
              </p>
              <div className="gonderim-detay-kargo-fields">
                <div className="gonderim-detay-kargo-field">
                  <KargoFirmasiField
                    idPrefix="gonderim-kargo"
                    value={kargoFirmasi}
                    onChange={setKargoFirmasi}
                    disabled={actionLoading}
                    required
                  />
                </div>
                <div className="gonderim-detay-kargo-field">
                  <label htmlFor="gonderim-kargo-takip">Takip Numarası</label>
                  <input
                    id="gonderim-kargo-takip"
                    type="text"
                    value={kargoTakipNo}
                    onChange={(e) => setKargoTakipNo(e.target.value)}
                    placeholder="Kargo takip numarası"
                    maxLength={100}
                    disabled={actionLoading}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="gonderim-detay-kargo-actions">
                <button type="submit" className="primary-btn" disabled={actionLoading}>
                  {actionLoading ? 'Kaydediliyor...' : 'Kargoya Ver'}
                </button>
              </div>
            </form>
          )}

          <div className="gonderim-detay-section">
            <h3>Parçalar</h3>
            <div className="gonderim-detay-table-wrap">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Parça</th>
                    <th>Kod</th>
                    <th style={{ textAlign: 'center' }}>Adet</th>
                    <th style={{ textAlign: 'center' }}>Öz / Kons.</th>
                  </tr>
                </thead>
                <tbody>
                  {pozisyonlar.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-gray)' }}>
                        Parça kaydı yok
                      </td>
                    </tr>
                  ) : (
                    pozisyonlar.map((p, idx) => (
                      <tr key={p.icerikId || idx}>
                        <td>{p.parcaAdi || '—'}</td>
                        <td>{p.parcaKodu || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{p.adet ?? 0}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span>{p.ozAdet ?? '—'}</span>
                          <span style={{ margin: '0 4px', color: 'var(--text-gray)' }}>/</span>
                          <span>{p.konsinyeAdet ?? '—'}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose} disabled={actionLoading}>
            Kapat
          </button>
          {canTeslimAl && (
            <button type="button" className="primary-btn" onClick={onTeslimAl} disabled={actionLoading}>
              {actionLoading ? 'İşleniyor...' : 'Teslim Al'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GonderimDetayModal;
